"use client";

import { useEffect, useRef } from "react";
import { ProgressProvider } from "@bprogress/next/app";
import { useUser } from "@clerk/nextjs";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";
import { getActivePartyIdFromPathname } from "@/lib/posthog-party-context";
import { AppSplashController } from "@/components/app-splash-controller";
import { NativeStatusBarMetaSync } from "@/components/native-status-bar-meta-sync";
import { AuthFlowErrorTracker } from "@/components/auth-flow-error-tracker";
import { ClerkLoadRecovery } from "@/components/clerk-load-recovery";
import { NativeAppleSignInBridge } from "@/components/native-apple-sign-in-bridge";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { PwaRefresh } from "@/components/pwa-refresh";
import { scheduleIdleDocumentScrollLockRestore } from "@/lib/document-scroll-lock";
import { useAnalyticsConsent } from "@/lib/use-analytics-consent";
import {
  shouldIdentifyPostHogUser,
  syncPostHogBrowserDistinctIdCookie,
} from "@/lib/posthog-browser";

function ScrollToTopOnRouteChange() {
  const pathname = usePathname();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    return scheduleIdleDocumentScrollLockRestore(
      document,
      (callback, delayMs) => window.setTimeout(callback, delayMs),
      (id) => window.clearTimeout(id),
    );
  }, [pathname]);

  return null;
}

function PostHogIdentifier() {
  const { user, isLoaded } = useUser();
  const { consent, isReady } = useAnalyticsConsent();
  const pathname = usePathname();
  const activePartyId = getActivePartyIdFromPathname(pathname);
  const userId = user?.id ?? null;
  const email =
    user?.primaryEmailAddress?.emailAddress ??
    user?.emailAddresses[0]?.emailAddress;
  const name = user?.fullName ?? user?.username ?? email ?? null;
  const identifiedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !isReady) return;
    if (consent !== "accepted") return;

    if (
      userId &&
      shouldIdentifyPostHogUser({
        consent,
        isLoaded,
        userId,
      })
    ) {
      posthog.identify(userId, {
        user_id: userId,
        email,
        name,
      });
      syncPostHogBrowserDistinctIdCookie();
      identifiedUserIdRef.current = userId;

      if (activePartyId) {
        posthog.register({ party_id: activePartyId });
        posthog.group("party", activePartyId, {
          party_id: activePartyId,
        });
      } else {
        posthog.unregister("party_id");
        posthog.resetGroups();
      }
    } else {
      posthog.unregister("party_id");
      posthog.resetGroups();
      // Only reset on an actual signed-in -> signed-out transition, not on
      // every anonymous mount/navigation (reset churns the anonymous id).
      if (identifiedUserIdRef.current) {
        identifiedUserIdRef.current = null;
        posthog.reset();
        syncPostHogBrowserDistinctIdCookie();
      }
    }
  }, [activePartyId, consent, email, isLoaded, isReady, name, userId]);

  return null;
}

const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProgressProvider
      height="4px"
      color="#c9a84c"
      options={{ showSpinner: false }}
      shallowRouting
    >
      <ScrollToTopOnRouteChange />
      <PostHogIdentifier />
      <AuthFlowErrorTracker />
      <ClerkLoadRecovery />
      <NativeAppleSignInBridge />
      <AppSplashController />
      <NativeStatusBarMetaSync />
      <PwaRefresh />
      <div className="ap-ptr-sheet">{children}</div>
      <CookieConsentBanner />
    </ProgressProvider>
  );
};

export default Providers;
